import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import FormDropdown from '../../../components/common/FormDropdown';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import {
  playerDetailsSchema,
  type PlayerDetailsFormValues,
} from '../validation/playerDetailsSchema';
import {
  PLAYER_ROLES,
  BATTING_STYLES,
  BOWLING_STYLES,
} from '../constants/playerDetailsConstants';
import './PlayerDetailsStep.scss';

const emptyPlayer: PlayerDetailsFormValues = {
  playerRole: '',
  battingStyle: '',
  bowlingStyle: '',
};

interface PlayerDetailsStepProps {
  initialValues?: PlayerDetailsFormValues;
  onNext: (data: PlayerDetailsFormValues) => void;
  onCompleteLater: () => void | Promise<void>;
  partialSaving?: boolean;
}

function PlayerDetailsStep({
  initialValues,
  onNext,
  onCompleteLater,
  partialSaving = false,
}: PlayerDetailsStepProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm<PlayerDetailsFormValues>({
    resolver: yupResolver(playerDetailsSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: initialValues ?? emptyPlayer,
  });

  useEffect(() => {
    reset(initialValues ?? emptyPlayer);
  }, [initialValues, reset]);

  const onSubmit = async (values: PlayerDetailsFormValues) => {
    setIsSubmitting(true);
    try {
      onNext(values);
    } catch {
      toast.error('Failed to continue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="player-details-form">
      <div className="player-details-fields">
        <div className="player-details-field">
          <label htmlFor="playerRole">
            Player Role <span className="req">*</span>
          </label>
          <Controller
            name="playerRole"
            control={control}
            render={({ field }) => (
              <FormDropdown
                id="playerRole"
                ref={field.ref}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={PLAYER_ROLES}
                hasError={!!errors.playerRole}
              />
            )}
          />
          {errors.playerRole && (
            <span className="field-error">{errors.playerRole.message}</span>
          )}
        </div>

        <div className="player-details-field">
          <label htmlFor="battingStyle">
            Batting Style <span className="req">*</span>
          </label>
          <Controller
            name="battingStyle"
            control={control}
            render={({ field }) => (
              <FormDropdown
                id="battingStyle"
                ref={field.ref}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={BATTING_STYLES}
                hasError={!!errors.battingStyle}
              />
            )}
          />
          {errors.battingStyle && (
            <span className="field-error">{errors.battingStyle.message}</span>
          )}
        </div>

        <div className="player-details-field">
          <label htmlFor="bowlingStyle">
            Bowling Style <span className="req">*</span>
          </label>
          <Controller
            name="bowlingStyle"
            control={control}
            render={({ field }) => (
              <FormDropdown
                id="bowlingStyle"
                ref={field.ref}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={BOWLING_STYLES}
                hasError={!!errors.bowlingStyle}
              />
            )}
          />
          {errors.bowlingStyle && (
            <span className="field-error">{errors.bowlingStyle.message}</span>
          )}
        </div>
      </div>

      <div className="player-details-actions">
        <button
          type="button"
          className="btn-complete-later"
          onClick={() => void onCompleteLater()}
          disabled={partialSaving}
        >
          {partialSaving ? 'Saving…' : 'Complete Profile Later'}
        </button>
        <button
          type="submit"
          className="btn-next"
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? 'Saving...' : 'Next'}
        </button>
      </div>
    </form>
  );
}

export default PlayerDetailsStep;
